import { useState, useEffect } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Package, ShoppingCart, Users, Wrench, ArrowLeftRight,
  Menu, X, ChevronLeft, LogOut, BarChart3, Boxes, FileEdit, Settings, Ticket, FolderOpen, HeadphonesIcon, MessageCircle, Heart,
  Store, ChevronDown, Megaphone, Globe, Briefcase, Plus, Smartphone, BookText, Search
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import fixswiftLogo from "@/assets/fixswift-logo.svg";

/* ── Grouped Navigation ── */
interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<any>;
}

interface NavGroup {
  label: string;
  icon: React.ComponentType<any>;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    label: "Catalog",
    icon: Package,
    items: [
      { to: "/admin/products", label: "Products", icon: Package },
      { to: "/admin/categories", label: "Categories", icon: FolderOpen },
      { to: "/admin/inventory", label: "Inventory", icon: Boxes },
    ],
  },
  {
    label: "Orders",
    icon: ShoppingCart,
    items: [
      { to: "/admin/orders", label: "All Orders", icon: ShoppingCart },
    ],
  },
  {
    label: "Customers",
    icon: Users,
    items: [
      { to: "/admin/customers", label: "All Customers", icon: Users },
      { to: "/admin/leads", label: "Leads", icon: MessageCircle },
    ],
  },
  {
    label: "Marketing",
    icon: Megaphone,
    items: [
      { to: "/admin/coupons", label: "Coupons", icon: Ticket },
      { to: "/admin/happy-customers", label: "Testimonials", icon: Heart },
    ],
  },
  {
    label: "Website",
    icon: Globe,
    items: [
      { to: "/admin/cms", label: "Content", icon: FileEdit },
      { to: "/admin/blogs", label: "Blog Posts", icon: BookText },
      { to: "/admin/seo", label: "SEO Settings", icon: Search },
      { to: "/admin/settings", label: "Settings", icon: Settings },
    ],
  },
  {
    label: "Services",
    icon: Briefcase,
    items: [
      { to: "/admin/repair", label: "Repairs", icon: Wrench },
      { to: "/admin/buyback", label: "Buyback Requests", icon: ArrowLeftRight },
      { to: "/admin/buyback-settings", label: "Sell Models", icon: Smartphone },
      { to: "/admin/support", label: "Support", icon: HeadphonesIcon },
    ],
  },
];

const topItems: NavItem[] = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
];

const allNavItems = [
  ...topItems,
  ...navGroups.flatMap((g) => g.items),
];

const AdminLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set(["Catalog", "Orders"]));
  const { signOut, profile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const initials = (profile?.full_name || "A").slice(0, 2).toUpperCase();
  const currentPage = allNavItems.find((item) => location.pathname.startsWith(item.to));

  // Auto-expand group containing active route
  const activeGroup = navGroups.find((g) => g.items.some((i) => location.pathname.startsWith(i.to)));

  // Auto-expand active group on route change
  useEffect(() => {
    if (activeGroup && !openGroups.has(activeGroup.label)) {
      setOpenGroups((prev) => new Set([...prev, activeGroup.label]));
    }
  }, [location.pathname]);

  const toggleGroup = (label: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const renderNavLink = (item: NavItem) => (
    <NavLink
      key={item.to}
      to={item.to}
      onClick={() => setMobileOpen(false)}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium transition-all duration-200 group relative",
          collapsed && "justify-center px-2",
          isActive
            ? "bg-primary/12 text-primary font-semibold shadow-sm shadow-primary/5"
            : "text-sidebar-foreground/50 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
        )
      }
      title={collapsed ? item.label : undefined}
    >
      <item.icon className={cn("h-[18px] w-[18px] shrink-0 transition-transform duration-200 group-hover:scale-110")} strokeWidth={1.8} />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </NavLink>
  );

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden transition-opacity duration-300"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex flex-col border-r border-sidebar-border/50 bg-sidebar text-sidebar-foreground transition-all duration-300 lg:relative",
          collapsed ? "w-[64px]" : "w-[240px]",
          mobileOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full lg:translate-x-0"
        )}
      >
        {/* Brand Header */}
        <div className="flex h-14 items-center justify-between border-b border-sidebar-border/30 px-3">
          {!collapsed ? (
            <div className="flex items-center gap-2.5">
              <img src={fixswiftLogo} alt="FixSwift" className="h-8 w-8 rounded-lg" />
              <span className="font-heading text-sm font-bold text-sidebar-foreground tracking-tight">FixSwift</span>
            </div>
          ) : (
            <img src={fixswiftLogo} alt="FixSwift" className="mx-auto h-8 w-8 rounded-lg" />
          )}
          <Button
            variant="ghost" size="icon"
            className="h-7 w-7 text-sidebar-foreground/30 hover:bg-sidebar-accent hover:text-sidebar-foreground hidden lg:flex rounded-md"
            onClick={() => setCollapsed(!collapsed)}
          >
            <ChevronLeft className={cn("h-3.5 w-3.5 transition-transform duration-300", collapsed && "rotate-180")} />
          </Button>
          <Button
            variant="ghost" size="icon"
            className="h-8 w-8 text-sidebar-foreground hover:bg-sidebar-accent lg:hidden rounded-md"
            onClick={() => setMobileOpen(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto custom-scrollbar px-2.5 py-3 space-y-0.5">
          {/* Top-level items */}
          {topItems.map(renderNavLink)}

          {!collapsed && <div className="h-px bg-sidebar-border/30 my-3" />}

          {/* Grouped sections */}
          {navGroups.map((group) => {
            const isOpen = openGroups.has(group.label) || activeGroup?.label === group.label;
            const hasActive = group.items.some((i) => location.pathname.startsWith(i.to));

            if (collapsed) {
              return group.items.map(renderNavLink);
            }

            return (
              <div key={group.label} className="space-y-0.5">
                <button
                  onClick={() => toggleGroup(group.label)}
                  className={cn(
                    "flex w-full items-center justify-between rounded-lg px-3 py-2 text-[10.5px] font-bold uppercase tracking-[0.08em] transition-colors duration-200",
                    hasActive
                      ? "text-sidebar-foreground/70"
                      : "text-sidebar-foreground/30 hover:text-sidebar-foreground/50"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <group.icon className="h-3.5 w-3.5" strokeWidth={1.8} />
                    <span>{group.label}</span>
                  </div>
                  <ChevronDown className={cn("h-3 w-3 transition-transform duration-300 ease-out", isOpen && "rotate-180")} />
                </button>

                <div
                  className={cn(
                    "overflow-hidden transition-all duration-300 ease-out",
                    isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
                  )}
                >
                  <div className="pl-1.5 space-y-0.5 pb-1">
                    {group.items.map(renderNavLink)}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

        {/* User Footer */}
        <div className="border-t border-sidebar-border/30 p-2.5 space-y-1">
          {!collapsed && (
            <div className="flex items-center gap-2.5 px-2 py-2 rounded-lg bg-sidebar-accent/30">
              <Avatar className="h-8 w-8 border-2 border-sidebar-border/20 shadow-sm">
                <AvatarImage src={profile?.profile_image || ""} />
                <AvatarFallback className="bg-primary/15 text-primary text-[10px] font-bold">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-semibold text-sidebar-foreground truncate">{profile?.full_name || "Admin"}</p>
                <p className="text-[10px] text-sidebar-foreground/35 truncate">{profile?.email || ""}</p>
              </div>
            </div>
          )}
          <button
            onClick={signOut}
            className={cn(
              "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-[12px] text-sidebar-foreground/40 hover:bg-red-500/10 hover:text-red-400 transition-all duration-200",
              collapsed && "justify-center px-2"
            )}
          >
            <LogOut className="h-4 w-4 shrink-0" strokeWidth={1.8} />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex flex-1 flex-col min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border/30 bg-background/95 backdrop-blur-md px-3 sm:px-5">
          <Button
            variant="ghost" size="icon"
            className="h-9 w-9 rounded-lg lg:hidden active:scale-95 transition-transform"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          {currentPage && (
            <div className="hidden lg:flex items-center gap-2">
              <currentPage.icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.8} />
              <span className="text-sm font-semibold text-foreground">{currentPage.label}</span>
            </div>
          )}
          <div className="flex-1" />
          <div className="flex items-center gap-1.5 sm:gap-2">
            <Button
              size="sm"
              className="h-8 rounded-lg text-xs font-semibold gap-1.5 hidden sm:flex shadow-sm"
              onClick={() => navigate("/admin/products")}
            >
              <Plus className="h-3.5 w-3.5" /> Add Product
            </Button>
            <NavLink to="/" className="text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-muted/50 active:scale-95">
              <Store className="h-4 w-4" />
              <span className="hidden sm:inline">Store</span>
            </NavLink>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
